import type { ActionType, ClientApiApi as AppClient, Interaction, User } from "@autointel/client-api";
import { DeviceType } from "@autointel/whiz-models";
import type { TenantCustomization } from "./tenant/TenantCustomization";

function isChildElementOfWhizChat(element: HTMLElement): boolean {
    if ("whiz-chat" === element.id) {
        return true;
    }

    if (!element.parentElement) {
        return false;
    }

    return isChildElementOfWhizChat(element.parentElement);
}

export default class InteractionsListener {
    _app?: AppClient;
    _user?: User;
    _tenantCustomization?: TenantCustomization;
    unsentInteractions: Interaction[] = [];

    get app(): AppClient | undefined {
        return this._app;
    }

    set app(value: AppClient | undefined) {
        this._app = value;
        if (this._app && this._user) {
            this.sendUnsentInteractions();
        }
    }

    get user(): User | undefined {
        return this._user;
    }

    set user(value: User | undefined) {
        this._user = value;
        if (this._app && this._user) {
            this.sendUnsentInteractions();
        }
    }

    constructor(tenantCustomization?: TenantCustomization) {
        this._tenantCustomization = tenantCustomization;
        this.attachPageListenerForInteractions(this._tenantCustomization?.getInteractionTagNames() ?? undefined);
        this.setupUrlChangeListener();
    }

    sendUnsentInteractions() {
        if (!this.app || !this.user) {
            console.warn("SDK not initialized");
            return;
        }

        this.unsentInteractions.forEach((interaction) => {
            this.app!.postInteraction({
                interaction: {
                    ...interaction,
                    context: {
                        ...interaction.context,
                        browsingSession: {
                            id: `${this.user!.id}-${interaction.timestamp}`,
                            ...((window.navigator as any).userAgentData?.mobile && {
                                deviceType: DeviceType.MOBILE,
                            }),
                        },
                        user: {
                            ...this.user,
                            id: this.user!.id,
                            userAgent: window.navigator.userAgent,
                        },
                    },
                },
            });
        });
    }

    async attachPageListenerForInteractions(tagNames: string[] = ["a", "button", "div", "span", "video"]) {
        if (typeof window === "undefined") {
            return;
        }

        document.body.addEventListener("mousedown", (event) => {
            const path = event.composedPath();
            const target = path.find((element) => {
                if (!(element instanceof HTMLElement)) {
                    return false;
                }
                return tagNames.includes(element.tagName?.toLowerCase()) && !isChildElementOfWhizChat(element);
            }) as HTMLElement | undefined;

            if (target) {
                const { tagName } = target;
                const timestamp = Date.now();
                const interaction: Interaction = {
                    itemId: `${window.location.pathname}-body-${tagName}`,
                    context: {
                        browsingSession: {
                            id: `${this.user?.id}-${timestamp}`,
                            ...((window.navigator as any).userAgentData?.mobile && {
                                deviceType: DeviceType.MOBILE,
                            }),
                        },
                        user: {
                            ...this.user,
                            id: this.user?.id ?? "",
                            userAgent: window.navigator.userAgent,
                        },
                        source: {
                            url: window.location.href,
                            name: document.title,
                        },
                        attributes: {
                            textContent: target.textContent,
                            ...Object.fromEntries(
                                Object.values(target.attributes).map((attr) => [attr.name, attr.value]),
                            ),
                        },
                    },
                    action: "SELECT" as ActionType,
                    timestamp,
                };

                if (!this.app || !this.user) {
                    console.warn("SDK not initialized");
                    this.unsentInteractions.push(interaction);
                    return;
                }

                this.app.postInteraction({
                    interaction,
                });
            }
        });
    }

    setupUrlChangeListener() {
        if (typeof window === "undefined" || !this._tenantCustomization?.getUrlListener) {
            return;
        }

        let lastUrl = window.location.href;

        const handleUrlChange = () => {
            const currentUrl = window.location.href;
            if (currentUrl !== lastUrl) {
                lastUrl = currentUrl;
                this._tenantCustomization?.getUrlListener?.(currentUrl, this);
            }
        };

        window.addEventListener("popstate", handleUrlChange);

        const originalPushState = history.pushState;
        history.pushState = function (...args) {
            originalPushState.apply(this, args);
            handleUrlChange();
        };

        const originalReplaceState = history.replaceState;
        history.replaceState = function (...args) {
            originalReplaceState.apply(this, args);
            handleUrlChange();
        };
    }
}
